﻿import React, { useRef, useEffect, useState } from 'react';
import { useAudio } from './hooks/useAudio';
import { audioEngine } from './features/audio/audioEngine';
import Mixer from './components/Mixer';
import Transport from './components/Transport';
import SongList from './components/SongList';

export default function App() {
  const { isRunning, pitchData } = useAudio();
  const canvasRef = useRef(null);
  const [progress, setProgress] = useState(0);

  // Visualizer & Progress Loop
  useEffect(() => {
    // We run loop always for Transport updates even if paused to catch seek updates
    let animId;
    const canvas = canvasRef.current;

    // Safety check for canvas existence
    if (!canvas) return; // Wait for mount
    const ctx = canvas.getContext('2d');

    const draw = () => {
      animId = requestAnimationFrame(draw);

      // Update Progress from Transport
      if (audioEngine.mode === 'player') {
        const p = audioEngine.getProgress();
        // Only update if not dragging (handled by local seek) - TODO: differentiate?
        // For now, simple update.
        setProgress(p > 1 ? 1 : p);
      }

      // Drawing Logic
      const bufferLength = audioEngine.analyser ? audioEngine.analyser.frequencyBinCount : 0;
      if (bufferLength > 0) {
        const dataArray = new Uint8Array(bufferLength);
        audioEngine.analyser.getByteFrequencyData(dataArray);

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const barWidth = (canvas.width / bufferLength) * 2.5;
        let x = 0;
        for (let i = 0; i < bufferLength; i++) {
          const barHeight = dataArray[i] / 2;
          const r = barHeight + 25 * (i / bufferLength);
          const g = 250 * (i / bufferLength);
          const b = 50;
          ctx.fillStyle = `rgb(${r},${g},${b})`;
          ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
          x += barWidth + 1;
        }
      }
    };
    draw();
    return () => cancelAnimationFrame(animId);
  }, []); // Run once on mount

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const p = Math.max(0, Math.min(1, x / rect.width));
    setProgress(p);
    audioEngine.seek(p);
  };

  return (
    // ULTRA HEAVY OUTER CASE
    <div className="h-screen w-screen bg-[#050505] flex items-center justify-center p-12 overflow-hidden select-none">

      {/* THE CASE */}
      <div className="relative w-full max-w-[1400px] h-full max-h-[900px] flex flex-col bg-gradient-to-br from-[#2a2a2a] via-[#111] to-[#050505] rounded-2xl border-[30px] border-[#151515] ring-[1px] ring-white/10 shadow-[0_50px_100px_black,inset_0_0_100px_black]">

        {/* Realistic Lighting/Bevels on Border */}
        {/* Top/Left Highlight (Light Source) */}
        <div className="absolute inset-[-30px] rounded-2xl pointer-events-none border-[2px] border-t-white/30 border-l-white/20 border-b-black border-r-black mix-blend-overlay" />
        {/* Inner Bevel Depth */}
        <div className="absolute inset-0 pointer-events-none border-[4px] border-[#0a0a0a] shadow-[inset_0_0_20px_black]" />

        {/* Screw Decorations */}
        <div className="absolute top-[-20px] left-[-20px] w-8 h-8 rounded-full bg-gradient-to-br from-[#333] to-[#111] shadow-[0_2px_5px_black] flex items-center justify-center z-50">
          <div className="w-4 h-1 bg-[#0a0a0a] rotate-45 shadow-[inset_0_1px_1px_black]" />
        </div>
        <div className="absolute top-[-20px] right-[-20px] w-8 h-8 rounded-full bg-gradient-to-br from-[#333] to-[#111] shadow-[0_2px_5px_black] flex items-center justify-center z-50">
          <div className="w-4 h-1 bg-[#0a0a0a] rotate-45 shadow-[inset_0_1px_1px_black]" />
        </div>
        <div className="absolute bottom-[-20px] left-[-20px] w-8 h-8 rounded-full bg-gradient-to-br from-[#333] to-[#111] shadow-[0_2px_5px_black] flex items-center justify-center z-50">
          <div className="w-4 h-1 bg-[#0a0a0a] rotate-45 shadow-[inset_0_1px_1px_black]" />
        </div>
        <div className="absolute bottom-[-20px] right-[-20px] w-8 h-8 rounded-full bg-gradient-to-br from-[#333] to-[#111] shadow-[0_2px_5px_black] flex items-center justify-center z-50">
          <div className="w-4 h-1 bg-[#0a0a0a] rotate-45 shadow-[inset_0_1px_1px_black]" />
        </div>

        {/* --- MAIN INTERFACE --- */}

        {/* TOP AREA (35%) */}
        <div className="h-[35%] flex border-b border-black shadow-lg z-10 bg-[#0c0c0c]">
          {/* Left 1/3: Playlist */}
          <div className="w-1/3 h-full border-r border-white/5 bg-[#111]">
            <SongList />
          </div>

          {/* Right 2/3: Visualizer */}
          <div className="w-2/3 h-full bg-black relative flex items-end justify-center shadow-[inset_0_0_50px_black] overflow-hidden">
            {/* Glass Reflection */}
            <div className="absolute inset-0 bg-gradient-to-tr from-white/5 via-transparent to-transparent pointer-events-none z-20" />

            <div className="absolute top-4 right-4 text-right mix-blend-screen z-10">
              <div className="text-5xl font-extralight text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)]">
                {isRunning && pitchData.note !== '-' ? pitchData.note : '--'}
              </div>
              <div className="font-mono text-cyan-700 text-sm mt-1">
                {isRunning ? `${pitchData.frequency.toFixed(1)} Hz` : 'STANDBY'}
              </div>
            </div>

            <canvas ref={canvasRef} width={800} height={200} className="w-full h-full opacity-80 mix-blend-screen" />
          </div>
        </div>

        {/* MIDDLE: Slider Progress Bar (5%) */}
        <div
          className="h-[5%] bg-[#080808] border-y border-black relative group cursor-pointer shadow-inner flex items-center px-0"
          onClick={handleSeek}
        >
          {/* Track Line */}
          <div className="absolute left-0 right-0 h-[2px] bg-[#333] shadow-[0_1px_0_rgba(255,255,255,0.1)] group-hover:bg-[#444] transition-colors" />

          {/* Filled Progress */}
          <div
            style={{ width: `${progress * 100}%` }}
            className="absolute left-0 h-[2px] bg-cyan-500 shadow-[0_0_10px_cyan]"
          />

          {/* Knob Thumb */}
          <div
            style={{ left: `${progress * 100}%` }}
            className="absolute w-4 h-4 -ml-2 rounded-full bg-gradient-to-b from-gray-200 to-gray-400 border border-black shadow-[0_2px_4px_black] z-20 hover:scale-110 transition-transform"
          >
            <div className="absolute top-1 left-1 right-1 h-1 bg-white/50 rounded-full" />
          </div>

          <div className="absolute inset-0 flex items-center justify-center text-[9px] font-mono text-[#333] pointer-events-none tracking-[0.3em]">
            TIMELINE
          </div>
        </div>

        {/* BOTTOM AREA (60%) */}
        <div className="flex-1 flex bg-gradient-to-b from-[#1c1c1c] to-[#111]">
          {/* Left 2/3: Mixer */}
          <div className="w-2/3 p-6 border-r border-black relative shadow-[inset_20px_0_40px_rgba(0,0,0,0.5)]">
            <Mixer />
          </div>

          {/* Right 1/3: Controls */}
          <div className="w-1/3 p-6 bg-leather-texture relative shadow-[inset_0_0_50px_black] flex flex-col justify-between">
            <Transport />

            {/* Branding Plate */}
            <div className="flex justify-center mb-4">
              <div className="bg-gradient-to-b from-[#0a0a0a] to-[#222] px-8 py-2 rounded border border-white/5 shadow-[0_2px_4px_black,inset_0_1px_0_rgba(255,255,255,0.1)]">
                <div className="text-xs font-bold text-gray-500 tracking-[0.3em] uppercase drop-shadow-sm">
                  Harmony<span className="text-cyan-700">Trainer</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
